import React, { Fragment, useRef, useState, useEffect, useContext } from 'react';
import { AuthContext } from "../contexts/auth";
import { Dialog, Transition } from '@headlessui/react';
import ComboboxAutocomplete from './combobox_autocomplete';
import { listOrganizationalUnits } from "../services/api_org_unit";
import moment from 'moment';
import { TrashIcon } from '@heroicons/react/24/outline';

const OuSelectionPair = ({ onClose, justification, onSelectOUtoPair }) => {
    const { token } = useContext(AuthContext);
    const cancelButtonRef = useRef(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [tempSelectedUnit, setTempSelectedUnit] = useState(null);
    const [units, setUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [startDay, setStartDay] = useState(formatDate(justification?.datetime_from) || '');
    const [endDay, setEndDay] = useState(formatDate(justification?.datetime_to) || '');
    const [startTime, setStartTime] = useState(formatTime(justification?.datetime_from) || '');
    const [endTime, setEndTime] = useState(formatTime(justification?.datetime_to) || '');
    

    function formatDate(dateString) {
        return moment(dateString).format('DD/MM/YYYY');
    }

    function formatTime(dateString) {
        return moment(dateString).format('HH:mm');
    }

    useEffect(() => {
        async function fetchData() {
            try {
                setIsLoading(true);
                const unitsData = await listOrganizationalUnits(token, true);
                setUnits(unitsData.data);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching units:', error);
                setError(true);
                setIsLoading(false);
            }
        }
        fetchData();
    }, [token]);

    const handleSelectUnit = (selected) => {
        setTempSelectedUnit(selected);
    };

    const handleConfirmUnit = () => {
        setSelectedUnit(tempSelectedUnit);
        onSelectOUtoPair(tempSelectedUnit);
        onClose();
    };

    const handleDeleteSelectedUnit = () => {
        setTempSelectedUnit(null);
    };

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 overflow-y-auto" onClose={() => {}} initialFocus={cancelButtonRef} style={{ zIndex: '100' }}>
                <div className="flex items-start justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-2/3 sm:w-1/3 mt-20 sm:mt-30 top-0">
                            <div className="text-center flex items-center justify-center bg-indigo-200 p-2">
                                <Dialog.Title as="h3" className="text-lg font-semibold text-indigo-700">
                                    Seleziona l'Unità Organizzativa
                                </Dialog.Title>
                            </div>

                            <div className='px-6 py-2 mt-2 font-semibold text-sm text-indigo-800 uppercase'>
                                Richiesta selezionata:
                            </div>

                            <div className="px-6">
                                <div className='grid grid-cols-2 gap-2 w-full'>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dipendente
                                    </div>
                                    <div className='col-span-1 text-sm flex items-center'>
                                        {justification?.employee?.last_name && justification?.employee?.first_name ? `${justification?.employee?.last_name} ${justification?.employee?.first_name}` : justification?.employee?.cf}
                                    </div>
                                            
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dal giorno
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justification && justification?.datetime_from ? (
                                            <div className="text-sm text-gray-600">{startDay}</div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Al giorno
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justification && justification?.datetime_to? (
                                            <div className="text-sm text-gray-600">{endDay}</div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>

                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Giustificativo
                                    </div>
                                    <div className='col-span-1 text-sm flex items-center font-semibold text-indigo-600'>
                                        {justification && justification?.justification?.justification?.description ? (
                                            <div className="text-sm text-gray-600">{justification?.justification?.justification?.description}</div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>

                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dalle ore
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justification && justification.datetime_from ? (
                                            <div className="text-sm text-gray-600">{startTime}</div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Alle ore
                                    </div>
                                    <div className='col-span-1 text-sm items-center'>
                                        {justification && justification.datetime_to ? (
                                            <div className="text-sm text-gray-600">{endTime}</div>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                </div>

                                {!tempSelectedUnit ? (
                                    <div className='my-3 pb-5 bg-white w-full' style={{ zIndex: '25'}}>
                                        <h3 className="py-2 mt-4 font-semibold text-sm text-indigo-800 uppercase">
                                            Seleziona l'Unità Organizzativa da associare al giustificativo
                                        </h3>
                                        <ComboboxAutocomplete
                                            options={units}
                                            bgActiveColor="bg-indigo-100"
                                            textActiveColor="text-gray-900"
                                            placeholder="Seleziona l'Unità Organizzativa"
                                            onChange={handleSelectUnit}
                                            value={tempSelectedUnit}
                                            positionOptions='relative'
                                        />
                                    </div>
                                ) : (
                                    <div className="my-4 flex justify-between items-center bg-gray-50 p-3 rounded-md">
                                        <span className="text-sm font-medium text-gray-700 uppercase align-center">
                                            Unità selezionata: <span className='font-normal text-gray-500'>{tempSelectedUnit.name}</span>
                                        </span>
                                        <button className="text-gray-700 font-bold px-1" title="Elimina" onClick={handleDeleteSelectedUnit}>
                                            <TrashIcon className="h-6 w-6 hover:text-red-500" /> 
                                        </button>
                                    </div>
                                )}
                                
                                <div className="text-center flex justify-center mb-4">
                                    <button
                                        className="bg-indigo-600 mb-2 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-500 focus:outline-none focus:bg-indigo-500 font-semibold me-3 w-32"
                                        onClick={handleConfirmUnit}
                                        disabled={!tempSelectedUnit}
                                    >
                                        Conferma
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="px-4 mb-2 py-2 rounded-md focus:outline-none font-semibold text-sm bg-white border border-solid-2 border-gray-200 shadow-md text-gray-700 w-32"
                                    >
                                        Chiudi
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default OuSelectionPair;
